import React, { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { useEvent } from 'react-use-event-hook'
import { ChannelPermissionsNameDescriptionModal } from '@components/ChannelSettings/ChannelPermissionsNameDescriptionForm'
import { InstalledBotRow } from '@components/InstalledBotRow/InstalledBotRow'
import { TownNotificationsButton } from '@components/NotificationSettings/NotificationsSettingsButton'
import { EditChannelName } from '@components/Panel/EditChannelName'
import { Panel } from '@components/Panel/Panel'
import { PanelButton } from '@components/Panel/PanelButton'
import {
    channelPermissionDescriptions,
    isChannelPermission,
} from '@components/SpaceSettingsPanel/rolePermissions.const'
import { Box, Icon, Paragraph, Stack, Text, TextButton } from '@ui'
import { useChannelEntitlements } from 'hooks/useChannelEntitlements'
import { useChannelIdFromPathname } from 'hooks/useChannelIdFromPathname'
import { useDevice } from 'hooks/useDevice'
import { useLeaveChannel } from 'hooks/useLeaveChannel'
import { useSortedChannels } from 'hooks/useSortedChannels'
import { CHANNEL_INFO_PARAMS, PATHS } from 'routes'
import { atoms } from 'ui/styles/atoms.css'
import { addressFromSpaceId } from 'ui/utils/utils'
import {
    Permission,
    useAppsInChannel,
    useChannelData,
    useChannelMembers,
    useChannelSettings,
    useChannelWithId,
    useConnectivity,
    useHasPermission,
    usePermissionOverrides,
    usePrefetchMultipleRoleDetails,
    useSpaceData,
} from 'use-towns-client'

import { ChannelMembersModal } from '../components/ChannelMembersPanel/ChannelMembersPanel'

import { usePanelActions } from './layouts/hooks/usePanelActions'
import { ChannelsRolesList } from './RoleRestrictedChannelJoinPanel'

export const ChannelInfoPanel = React.memo(() => {
    return (
        <Panel label="Channel Info">
            <ChannelInfo />
        </Panel>
    )
})

const InstalledAppsInChannel = ({
    spaceId,
    channelId,
    openBotProfile,
}: {
    spaceId: string | undefined
    channelId: string | undefined
    openBotProfile: (userId: string) => void
}) => {
    const { apps: installedAppsInChannel } = useAppsInChannel(spaceId ?? '', channelId ?? '')

    if (!installedAppsInChannel || installedAppsInChannel.length === 0) {
        return null
    }

    return (
        <Stack gap padding background="level2" rounded="sm">
            <Stack horizontal alignItems="center" width="100%">
                <Paragraph strong truncate color="default">
                    Bots
                </Paragraph>
            </Stack>
            <Stack gap="sm">
                {installedAppsInChannel.map(({ appId, appAddress }) => (
                    <InstalledBotRow
                        key={appId}
                        appAddress={appAddress}
                        openBotProfile={openBotProfile}
                    />
                ))}
            </Stack>
        </Stack>
    )
}

const ChannelInfo = () => {
    const { channel } = useChannelData()
    const { memberIds } = useChannelMembers()
    const { isTouch } = useDevice()
    const spaceData = useSpaceData()
    const { loggedInWalletAddress } = useConnectivity()

    const currentChannelId = useChannelIdFromPathname()
    const { sortedFavoriteChannels, sortedSpaceChannels } = useSortedChannels({
        spaceId: spaceData?.id,
        currentRouteId: currentChannelId,
    })

    const { hasPermission: canJoinChannel, isLoading: isLoadingCanJoinChannel } = useHasPermission({
        spaceId: spaceData?.id,
        channelId: channel?.id,
        walletAddress: loggedInWalletAddress,
        permission: Permission.Read,
    })

    const { hasPermission: canEditChannel } = useHasPermission({
        spaceId: spaceData?.id ?? '',
        walletAddress: loggedInWalletAddress ?? '',
        permission: Permission.AddRemoveChannels,
    })

    const { hasPermission: canEditRoles } = useHasPermission({
        spaceId: spaceData?.id ?? '',
        walletAddress: loggedInWalletAddress ?? '',
        permission: Permission.ModifySpaceSettings,
    })

    const navigate = useNavigate()
    const { leaveChannel } = useLeaveChannel()

    const isUserChannelMember = useMemo(() => {
        return loggedInWalletAddress && memberIds.includes(loggedInWalletAddress)
    }, [loggedInWalletAddress, memberIds])

    const { channelSettings } = useChannelSettings(spaceData?.id ?? '', channel?.id ?? '')
    const { hasSomeEntitlement } = useChannelEntitlements({
        spaceId: spaceData?.id,
        channelId: channel?.id,
    })

    const roles = channelSettings?.roles
    const roledIds = useMemo(() => roles?.map((r) => r.roleId) ?? [], [roles])
    usePrefetchMultipleRoleDetails(spaceData?.id, roledIds)

    const channelExists = channel !== undefined
    const room = useChannelWithId(channel?.id)
    const [activeModal, setActiveModal] = useState<'members' | 'settings' | undefined>(undefined)

    const onLeaveClick = useEvent(async () => {
        if (!channel || !spaceData) {
            return
        }

        await leaveChannel(channel.id, spaceData.id)

        const spaceAddress = addressFromSpaceId(spaceData.id)

        if (
            sortedFavoriteChannels.length > 1 ||
            (sortedFavoriteChannels.length === 1 && sortedFavoriteChannels[0].id !== channel.id)
        ) {
            if (sortedFavoriteChannels[0].id === channel.id && sortedFavoriteChannels.length > 1) {
                navigate(
                    `/${PATHS.SPACES}/${spaceAddress}/${PATHS.CHANNELS}/${sortedFavoriteChannels[1].id}/`,
                )
            } else {
                navigate(
                    `/${PATHS.SPACES}/${spaceAddress}/${PATHS.CHANNELS}/${sortedFavoriteChannels[0].id}/`,
                )
            }
        } else if (sortedSpaceChannels.length > 0) {
            if (sortedSpaceChannels[0].id === channel.id && sortedSpaceChannels.length > 1) {
                navigate(
                    `/${PATHS.SPACES}/${spaceAddress}/${PATHS.CHANNELS}/${sortedSpaceChannels[1].id}/`,
                )
            } else {
                navigate(
                    `/${PATHS.SPACES}/${spaceAddress}/${PATHS.CHANNELS}/${sortedSpaceChannels[0].id}/`,
                )
            }
        } else {
            navigate(`/${PATHS.SPACES}/${spaceAddress}/`)
        }
    })

    const { openPanel } = usePanelActions()

    const onMembersClick = useCallback(() => {
        if (isTouch) {
            setActiveModal('members')
        } else {
            openPanel(CHANNEL_INFO_PARAMS.DIRECTORY)
        }
    }, [isTouch, openPanel])

    const onShowChannelSettingsPopup = useEvent(() => {
        setActiveModal('settings')
    })

    const openProfilePanel = useEvent((userId: string) => {
        openPanel(CHANNEL_INFO_PARAMS.PROFILE, { profileId: userId })
    })

    const onHideChannelSettingsPopup = useEvent(() => {
        setActiveModal(undefined)
    })

    const onOpenEditChannelPermissionsPanel = useEvent(() => {
        openPanel(CHANNEL_INFO_PARAMS.EDIT_CHANNEL_PERMISSIONS)
    })

    const onOpenEditChannelSettingsPanel = useEvent(() => {
        openPanel(CHANNEL_INFO_PARAMS.EDIT_CHANNEL_RIVER_METADATA)
    })

    const onEditRolePermissions = useCallback(
        (roleId: number) => {
            openPanel(CHANNEL_INFO_PARAMS.EDIT_CHANNEL_PERMISSION_OVERRIDES, {
                channelId: channel?.id ?? '',
                roleId: roleId.toString(),
            })
        },
        [channel?.id, openPanel],
    )
    const spaceId = spaceData?.id
    const channelId = channel?.id

    return (
        <>
            <Stack gap>
                <Stack gap padding background="level2" rounded="sm">
                    <EditChannelName
                        canEdit={canEditChannel}
                        name={`#${channel?.label}`}
                        address={channel?.id ?? ''}
                        onEdit={onShowChannelSettingsPopup}
                    />
                </Stack>

                {roles && (
                    <Stack
                        padding
                        elevate
                        data-testid="channel-permission-details"
                        gap="md"
                        background="level2"
                        rounded="sm"
                    >
                        <Stack horizontal justifyContent="spaceBetween">
                            <Paragraph strong truncate color="default">
                                Channel Permissions
                            </Paragraph>
                            {canEditChannel && (
                                <TextButton
                                    data-testid="channel-permission-details-edit-button"
                                    onClick={onOpenEditChannelPermissionsPanel}
                                >
                                    Edit
                                </TextButton>
                            )}
                        </Stack>
                        {hasSomeEntitlement && (
                            <Stack horizontal alignItems="center" gap="xs" color="gray2">
                                <Icon type="lock" size="square_xs" color="gray2" />
                                <Text
                                    size={{
                                        mobile: 'sm',
                                    }}
                                >
                                    Only members of these roles can access:
                                </Text>
                            </Stack>
                        )}
                        <ChannelsRolesList
                            spaceId={spaceId}
                            canJoin={canJoinChannel}
                            isLoadingCanJoin={isLoadingCanJoinChannel}
                            roles={roles}
                            headerSubtitle={(role) =>
                                spaceId && channelId ? (
                                    <PermissionText
                                        spaceId={spaceId}
                                        channelId={channelId}
                                        roleId={role.roleId}
                                        permissions={role.permissions}
                                        showDiff={canEditRoles}
                                        key={role.roleId}
                                    />
                                ) : (
                                    role.permissions
                                        .map((p) => channelPermissionDescriptions[p]?.name ?? p)
                                        .join(', ')
                                )
                            }
                            onEditRolePermissions={canEditRoles ? onEditRolePermissions : undefined}
                        />
                    </Stack>
                )}

                <Stack gap padding background="level2" rounded="sm">
                    <Stack horizontal alignItems="center" width="100%">
                        <Paragraph strong truncate color="default">
                            Description
                        </Paragraph>
                        <Box grow />
                        {canEditChannel && (
                            <TextButton onClick={onShowChannelSettingsPopup}>Edit</TextButton>
                        )}
                    </Stack>
                    <Paragraph color="gray2">{room?.topic || 'No description'}</Paragraph>
                </Stack>
                <Stack gap padding background="level2" rounded="sm">
                    <Paragraph strong>Encryption</Paragraph>
                    <Box horizontal alignItems="center" gap="xs">
                        <Icon type="lock" size="square_sm" color="gray2" />
                        <Paragraph color="gray2">
                            {channelExists
                                ? 'This channel is end-to-end encrypted'
                                : 'This channel is not end-to-end encrypted'}
                        </Paragraph>
                    </Box>
                </Stack>

                <PanelButton disabled={memberIds.length === 0} onClick={onMembersClick}>
                    <Icon type="people" size="square_sm" color="gray2" />
                    <Paragraph color="default">
                        {memberIds.length === 0
                            ? 'No members'
                            : `${memberIds.length} member${memberIds.length > 1 ? `s` : ``}`}
                    </Paragraph>
                </PanelButton>
                <InstalledAppsInChannel
                    spaceId={spaceId}
                    channelId={channelId}
                    openBotProfile={openProfilePanel}
                />
                {channelId && (
                    <TownNotificationsButton
                        type="channel"
                        spaceId={spaceId}
                        channelId={channelId}
                    />
                )}

                {canEditChannel && (
                    <PanelButton onClick={onOpenEditChannelSettingsPanel}>
                        <Icon type="settings" size="square_sm" color="gray2" />
                        <Paragraph
                            fontWeight="medium"
                            color="default"
                            data-testid="edit-channel-settings-button"
                        >
                            Channel Settings
                        </Paragraph>
                    </PanelButton>
                )}

                {isUserChannelMember && (
                    <PanelButton tone="negative" onClick={onLeaveClick}>
                        <Icon type="logout" size="square_sm" />
                        <Paragraph color="error">Leave #{channel?.label}</Paragraph>
                    </PanelButton>
                )}
            </Stack>

            {activeModal === 'members' && (
                <ChannelMembersModal onHide={onHideChannelSettingsPopup} />
            )}
            {activeModal === 'settings' && spaceData && channel && (
                <ChannelPermissionsNameDescriptionModal onHide={onHideChannelSettingsPopup} />
            )}
        </>
    )
}

const PermissionText = (props: {
    spaceId: string
    roleId: number
    channelId: string
    permissions: Permission[]
    showDiff?: boolean
}) => {
    const { spaceId, roleId, channelId, permissions, showDiff } = props
    const { permissions: permissionOverrides } = usePermissionOverrides(spaceId, channelId, roleId)

    const result = useMemo(() => {
        if (!showDiff || !permissionOverrides) {
            // keep original order if there's not diff to show
            return {
                union: permissionOverrides ?? permissions,
                removed: [] as Permission[],
                added: [] as Permission[],
            }
        }

        const sets = {
            intersection: permissions.filter((p) => permissionOverrides?.includes(p)),
            removed: permissions.filter((p) => !permissionOverrides?.includes(p)),
            added: permissionOverrides?.filter((p) => !permissions.includes(p)) ?? [],
        }

        return {
            ...sets,
            union: [...sets.intersection, ...sets.removed, ...sets.added],
        }
    }, [showDiff, permissionOverrides, permissions])

    return (
        <Paragraph color="gray2" size="sm">
            {result.union.filter(isChannelPermission).map((p, index, arr) => {
                const className =
                    !showDiff || !permissionOverrides
                        ? ''
                        : atoms({
                              color: result.added.includes(p) ? 'default' : 'gray2',
                              textDecoration: result.removed.includes(p) ? 'lineThrough' : 'none',
                          })
                return (
                    <>
                        <span key={p} className={className}>
                            {channelPermissionDescriptions[p]?.name ?? p}
                        </span>
                        {index < arr.length - 1 && ', '}
                    </>
                )
            })}
        </Paragraph>
    )
}
